// UI hook — sizes a scrolling list so its bottom edge falls on a row boundary
// (polish.P28c r1 / r1b). The decision is `wholeRowScrollHeight`; this hook only
// measures the rendered rows and re-measures when they or their room change.

import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { wholeRowScrollHeight, type WholeRowFit } from './WholeRowScroll.transform';

/**
 * - `rows`: exactly this many whole rows (the ledger's three).
 * - `budget`: as many whole rows as `budgetRem` holds, at least `minRows`.
 * - `fill`: as many whole rows as the room left in the list's parent holds,
 *   when the card holding it is absolutely positioned (its height is set by its
 *   grid row, not by the list, so capping the list cannot shrink the room);
 *   otherwise `fallbackRem`.
 */
export type WholeRowScrollFit =
  | { kind: 'rows'; rows: number }
  | { kind: 'budget'; budgetRem: number; minRows: number }
  | { kind: 'fill'; fallbackRem: number; minRows: number };

function rootRemPx(): number {
  return Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
}

/** The list's card (a few levels up) is absolutely positioned. */
function inAbsoluteCard(from: HTMLElement, levels = 3): boolean {
  let node: HTMLElement | null = from;
  for (let step = 0; node && step < levels; step += 1, node = node.parentElement) {
    if (getComputedStyle(node).position === 'absolute') return true;
  }
  return false;
}

function budgetFor(el: HTMLElement, fit: Exclude<WholeRowScrollFit, { kind: 'rows' }>): number {
  if (fit.kind === 'budget') return fit.budgetRem * rootRemPx();
  const parent = el.parentElement;
  if (parent && inAbsoluteCard(parent)) {
    const room = parent.getBoundingClientRect().bottom - Number.parseFloat(getComputedStyle(parent).paddingBottom || '0');
    return room - el.getBoundingClientRect().top;
  }
  return fit.fallbackRem * rootRemPx();
}

export function useWholeRowScroll<T extends HTMLElement>(
  fit: WholeRowScrollFit,
  /** Rows inside the scroll element; its direct children by default (`tbody > tr` for a table). */
  rowSelector?: string,
): { ref: React.RefObject<T>; style: CSSProperties | undefined } {
  const ref = useRef<T>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const fitKey = JSON.stringify(fit);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const rowsOf = () => (rowSelector ? [...el.querySelectorAll<HTMLElement>(rowSelector)] : [...el.children] as HTMLElement[]);
    const measure = () => {
      const origin = el.getBoundingClientRect().top + el.clientTop - el.scrollTop;
      const rowTops = rowsOf().map((row) => row.getBoundingClientRect().top - origin);
      const rule: WholeRowFit = fit.kind === 'rows' ? { rows: fit.rows } : { budgetPx: budgetFor(el, fit), minRows: fit.minRows };
      const next = wholeRowScrollHeight(rowTops, el.scrollHeight, rule);
      const rounded = next === null ? null : Math.round(next * 100) / 100;
      setMaxHeight((current) => (current === rounded ? current : rounded));
    };
    const resize = new ResizeObserver(measure);
    const watch = () => {
      resize.disconnect();
      resize.observe(el);
      if (el.parentElement) resize.observe(el.parentElement);
      for (const row of rowsOf()) resize.observe(row);
    };
    const mutations = new MutationObserver(() => {
      watch();
      measure();
    });
    watch();
    measure();
    mutations.observe(el, { childList: true, subtree: true });
    return () => {
      resize.disconnect();
      mutations.disconnect();
    };
    // `fit` is compared by value through `fitKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey, rowSelector]);
  return { ref, style: maxHeight === null ? undefined : { maxHeight: `${maxHeight}px` } };
}
